import { ESPORTS } from '../constants/defaultValues';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { az, enGB } from 'date-fns/locale';
import { getTranslation, locale } from './translation';
import LocalStorage from './localStorage';
import moment from 'moment';
import { producerTypeEnum, ticketTypeEnum } from '../constants/enums';
import betSlipCalculator from './betSlipCalculator';
import { store } from '../store/index';
import { setAuthenticated } from '../store/actions/auth';
import { EVENT_FILTER_OPTIONS } from '../constants/event-filters';

function parseValue(v) {
    // extract number (defaults to 0 if not present)
    let n = +(v.match(/\d+$/) || [0])[0];
    let str = v.replace(n, ''); // extract string part
    return [str, n];
}

function sortOutcomeValues(outcomes) {
    if (outcomes && outcomes.length > 0) {
        let newOutcomes = [...outcomes];

        newOutcomes.sort(function (a, b) {
            a = parseValue(a.outcomeRefId);
            b = parseValue(b.outcomeRefId);

            // compare string part alphabetically
            let result = a[0].localeCompare(b[0]);
            // if the string part is the same, compare the number part
            return result === 0 ? a[1] - b[1] : result;
        });

        return newOutcomes;
    }

    // console.log('SORTED OUTCOMES', sortedValues);
    return [];
}

function mbnColorPicker(count) {
    if (count === 0) return '#2a2f3a';
    if (count === 1) return '#2e66bf';
    if (count === 2) return '#A29534';
    if (count === 3) return '#049616';
    return '#2a2f3a';
}

export const generateQuery = filters => {
    const query = {};

    const getParam = (items, key, paramName) => {
        const itemId = items.find(item => item.selected).id;

        if (itemId !== key) {
            query[paramName] = itemId;
        }
    };

    if (filters.categories) {
        getParam(filters.categories, EVENT_FILTER_OPTIONS.categories.all.id, 'categoryId');
    }

    if (filters.leagues) {
        getParam(filters.leagues, EVENT_FILTER_OPTIONS.leagues.all.id, 'leagueId');
    }

    if (filters.periods) {
        const selectedPeriod = filters.periods.find(item => item.selected);
        if (selectedPeriod.id !== 'all') {
            const { startTime, endTime } = selectedPeriod.getRange();
            query.startDate = startTime;
            query.endDate = endTime;
        }
    }

    if (filters.orders) {
        query.sortId = filters.orders.find(order => order.selected).id;
    }

    return query;
};

export const checkEventsNextPage = (events, type, paginationLimit) => {
    let eventsCount = 0;

    if (type === EVENT_FILTER_OPTIONS.orders.byLeague.id) {
        events.forEach(season => {
            eventsCount += season.events?.length || 0;
        });
    } else if (
        type === EVENT_FILTER_OPTIONS.orders.byTime.id ||
        type === EVENT_FILTER_OPTIONS.orders.byMbn.id
    ) {
        eventsCount = events.length;
    }

    return eventsCount >= paginationLimit;
};

export const setDatePickerLocale = () => {
    if (locale === 'eng') {
        registerLocale('en-GB', enGB);
        setDefaultLocale('en-GB');
    } else if (locale === 'aze') {
        registerLocale('az', az);
        setDefaultLocale('az');
    }
};

export const getMoreMarketsUrl = ({ event }) => {
    if (!window.location.pathname.includes('/markets')) {
        LocalStorage.setItem('moreMarketsRedirectUrl', window.location.pathname);
    }

    const sportId = event.sportId || event.sportTypeRefId;

    const url = generateEventsUrl({
        isLive: event.live,
        isESports: isESportEvent(sportId),
        sportId,
    });

    return url + `/events/${encodeURIComponent(event.id)}/markets`;
};

export const getEventsUrl = ({ event }) => {
    const redirectUrl = LocalStorage.getItem('moreMarketsRedirectUrl');

    if (redirectUrl) return redirectUrl;

    const sportId = event.sportId || event.sportTypeRefId;

    return generateEventsUrl({
        isLive: event.live,
        isESports: isESportEvent(sportId),
        sportId,
    });
};

export const generateEventsUrl = ({ isLive, isESports, sportId }) => {
    let url = `${generateBaseSportsUrl({
        isLive,
        isESports,
    })}${encodeURIComponent(sportId)}`;

    return url;
};

export const generateBaseSportsUrl = ({ isLive, isESports }) => {
    let url = '/app/';

    if (isESports) {
        url += 'e-';
    }

    if (isLive) {
        url += 'live/';
    } else {
        url += 'sports/';
    }

    return url;
};

export const getSportIdParam = (pathname = '') => {
    return decodeURIComponent(pathname.split('/')[3]);
};

export const removeMoreMarketsRedirectUrl = () => {
    LocalStorage.removeItem('moreMarketsRedirectUrl');
};

export const generateCouponPrintDetails = ({ couponData, stakes, cashierCode }) => {
    const placementDate = moment.unix(couponData.creationDate).format('DD/MM/YYYY HH:mm:ss');
    const expireDate = moment
        .unix(couponData.creationDate)
        .add(1, 'days')
        .format('DD/MM/YYYY HH:mm:ss');

    let betType;

    switch (couponData.ticketType) {
        case ticketTypeEnum.SINGLE: {
            betType = getTranslation('betSlipTabsSingle');
            break;
        }
        case ticketTypeEnum.COMBO: {
            betType = getTranslation('betSlipTabsCombo');
            break;
        }
        case ticketTypeEnum.SYSTEM: {
            betType = getTranslation('betSlipTabsSystem');
            break;
        }
        default: {
            betType = '';
        }
    }

    let systemCombinations;

    if (couponData.ticketType === ticketTypeEnum.SYSTEM) {
        systemCombinations = betSlipCalculator
            .generateSystems(couponData.bets, stakes[ticketTypeEnum.SYSTEM])
            .map(combination => `${combination.id}/${combination.of}`)
            .join(', ');
    }

    return {
        couponNumber: couponData.couponNumber,
        cashierCode,
        placementDate,
        expireDate,
        ticketDetails: {
            betType,
            totalStake: couponData.totalStake,
            systemCombinations,
        },
    };
};

export const getProducerType = isLive =>
    isLive ? producerTypeEnum.LIVE : producerTypeEnum.PRE_MATCH;

export const isOnlyESport = betLines => betLines?.every(bL => !!ESPORTS[bL.sportTypeId]);
export const isESportBetLine = betLine => !!ESPORTS[betLine.sportTypeId];
export const isESportEvent = sportId => !!ESPORTS[sportId];

export { sortOutcomeValues, parseValue, mbnColorPicker };

export const logout = () => {
    if (window.location.pathname !== '/home') {
        store.dispatch(setAuthenticated(false));
        window.location = '/home';
        sessionStorage.clear();
        LocalStorage.clear();
    }
};

export const isVoucherMode = terminalInfo => {
    return terminalInfo && terminalInfo.currentBalance > 0;
};
